import { Rule } from "@ml-pa/react-apollo-form-tool/formValidation";
import { UnitInputCreate } from "raft/internal/schemaTypes";
import { UnitCreateAdditionalFields } from "raft/UnitForm";
import { ValidateFormPropsInner } from "@ml-pa/react-apollo-form-tool/formGen.model";
import translations from "translations";
import { checkFractionalDigits, extractRuleFunction, isFieldRequied } from "../utils";
import { UploadCertificateUnit } from "../../types";
import { Maybe } from "graphqlBase/types";

// ---------------- Typing --------------------
export type FullRule<T_FIELD extends keyof (UnitInputCreate & UnitCreateAdditionalFields)> = Rule<
  UnitInputCreate & UnitCreateAdditionalFields,
  ValidateFormPropsInner<
    UnitInputCreate & UnitCreateAdditionalFields,
    Partial<UnitInputCreate> & UnitCreateAdditionalFields
  >,
  T_FIELD
>;

export type PossibleGenericRule =
  | RuleFunction<UnitInputCreate & UnitCreateAdditionalFields>
  | {
      rule: RuleFunction<UnitInputCreate & UnitCreateAdditionalFields>;
      triggerFields?: string[];
    };

export type TriggerField = keyof (UnitInputCreate & UnitCreateAdditionalFields);
export type RuleFunction<T_DATA> = (params: {
  value: unknown;
  data: T_DATA;
}) => string | undefined | Promise<string | undefined>;

// Utility to check if normList includes 10 or higher
export function normListIncludes10OrHigher(normList?: string): boolean {
  if (!normList) return false;
  // Accepts comma or whitespace separated, or just substring
  return ["10", "20", "30"].some((n) => normList.includes(n));
}

// Utility to adapt a generic rule to the operation field rule signature
export function adaptGenericRuleForOperationField(
  ruleOrRuleObj:
    | (() => undefined)
    | (({
        value,
        data,
      }: {
        value: Maybe<string | number> | undefined;
        data: UnitInputCreate & UnitCreateAdditionalFields;
      }) => Promise<string | undefined> | string | undefined)
    | {
        rule:
          | (() => undefined)
          | (({
              value,
              data,
              ...rest
            }: {
              value: Maybe<string | number> | undefined;
              data: UnitInputCreate & UnitCreateAdditionalFields;
            }) => Promise<string | undefined> | string | undefined);
        makeOptional?: boolean;
        triggerFields?: string[];
      }
    | (({ value }: { value: Maybe<string | number> | undefined }) => string | undefined)
): (params: {
  value: unknown;
  data: UnitInputCreate & UnitCreateAdditionalFields;
}) => string | undefined | Promise<string | undefined> {
  // Extract the rule function from a potential rule object
  try {
    const rule = extractRuleFunction(ruleOrRuleObj);
    return ({ value, data }) => {
      if (typeof rule === "function") {
        // Handle different function signatures based on parameter count and structure
        if (rule.length === 0) {
          // No parameters required, but TypeScript expects at least one
          try {
            return (rule as () => string | undefined | Promise<string | undefined>)();
          } catch (e) {
            // If that fails, try with minimal parameters
            return (rule as ({ value }: { value?: undefined }) => string | undefined | Promise<string | undefined>)({
              value: undefined,
            });
          }
        } else {
          // Try with both value and data
          try {
            return (
              rule as ({
                value,
                data,
              }: {
                value: Maybe<string | number> | undefined;
                data: UnitInputCreate & UnitCreateAdditionalFields;
              }) => string | undefined | Promise<string | undefined>
            )({ value: value as Maybe<string | number> | undefined, data });
          } catch (e) {
            // If that fails, try with just value
            try {
              return (
                rule as ({
                  value,
                }: {
                  value: Maybe<string | number> | undefined;
                }) => string | undefined | Promise<string | undefined>
              )({ value: value as Maybe<string | number> | undefined });
            } catch (e2) {
              // Last resort - try with empty object
              return (rule as (params: Record<string, never>) => string | undefined | Promise<string | undefined>)({});
            }
          }
        }
      }
      // For non-function rules or other edge cases, return undefined
      return undefined;
    };
  } catch (error) {
    // Fall back to a no-op rule function that returns undefined when called
    return () => undefined;
  }
}

// ---------------- Rule Factory --------------------
export const fieldToSwitchFlagMap = {
  // Operation 1
  operation1MaxSwitchN10: "hasSwitchingOperation1",
  operation1MaxNumberN120: "hasSwitchingOperation1",
  operation1VoltageVariationFactor30: "hasSwitchingOperation1",
  operation1VoltageVariationFactor50: "hasSwitchingOperation1",
  operation1VoltageVariationFactor70: "hasSwitchingOperation1",
  operation1VoltageVariationFactor85: "hasSwitchingOperation1",
  operation1FlickerStepFactor30: "hasSwitchingOperation1",
  operation1FlickerStepFactor50: "hasSwitchingOperation1",
  operation1FlickerStepFactor70: "hasSwitchingOperation1",
  operation1FlickerStepFactor85: "hasSwitchingOperation1",
  // Operation 2
  operation2MaxSwitchN10: "hasSwitchingOperation2",
  operation2MaxNumberN120: "hasSwitchingOperation2",
  operation2VoltageVariationFactor30: "hasSwitchingOperation2",
  operation2VoltageVariationFactor50: "hasSwitchingOperation2",
  operation2VoltageVariationFactor70: "hasSwitchingOperation2",
  operation2VoltageVariationFactor85: "hasSwitchingOperation2",
  operation2FlickerStepFactor30: "hasSwitchingOperation2",
  operation2FlickerStepFactor50: "hasSwitchingOperation2",
  operation2FlickerStepFactor70: "hasSwitchingOperation2",
  operation2FlickerStepFactor85: "hasSwitchingOperation2",
  // Operation 3
  operation3MaxSwitchN10: "hasSwitchingOperation3",
  operation3MaxNumberN120: "hasSwitchingOperation3",
  operation3VoltageVariationFactor30: "hasSwitchingOperation3",
  operation3VoltageVariationFactor50: "hasSwitchingOperation3",
  operation3VoltageVariationFactor70: "hasSwitchingOperation3",
  operation3VoltageVariationFactor85: "hasSwitchingOperation3",
  operation3FlickerStepFactor30: "hasSwitchingOperation3",
  operation3FlickerStepFactor50: "hasSwitchingOperation3",
  operation3FlickerStepFactor70: "hasSwitchingOperation3",
  operation3FlickerStepFactor85: "hasSwitchingOperation3",
} as const;

export type OperationField = keyof typeof fieldToSwitchFlagMap;

const getEffectiveMaxActivePower = (data: UnitInputCreate & UnitCreateAdditionalFields) =>
  data.hasActivePowerRange ? data.maxActivePowerRange ?? data.minActivePowerRange : data.maxActivePower;

export function createOperationFieldRules<T_FIELD extends OperationField>(
  fieldName: T_FIELD,
  genericRules: Record<string, PossibleGenericRule | PossibleGenericRule[]>
): FullRule<T_FIELD>[] {
  const switchKey = fieldToSwitchFlagMap[fieldName];

  const genericRule = genericRules[fieldName as keyof typeof genericRules] as
    | PossibleGenericRule
    | PossibleGenericRule[];
  // Rule for switching operation condition (ASYNCHRONOUS/SYNCHRONOUS generators with norm ≥ 10)
  const switchingOperationRule: FullRule<T_FIELD> = {
    rule: ({ value, data }) => {
      const category = data.__category;
      const hasSwitching = data[switchKey as keyof (UnitInputCreate & UnitCreateAdditionalFields)];
      const isGeneratorWithHighNorm =
        (category === "ASYNCHRONOUS_GENERATOR" || category === "SYNCHRONOUS_GENERATOR") &&
        normListIncludes10OrHigher(data.__normList);
      const effectiveMaxActivePower = getEffectiveMaxActivePower(data);

      // Check if it's a generator with max power >= 30 (required fields)
      const isGenWithHighPower =
        isGeneratorWithHighNorm && typeof effectiveMaxActivePower === "number" && effectiveMaxActivePower >= 30;

      // Check if it's a generator with max power < 30 (optional fields)
      const isGenWithLowPower =
        isGeneratorWithHighNorm && typeof effectiveMaxActivePower === "number" && effectiveMaxActivePower < 30;

      // Apply strict validation for high power generators
      if (isGenWithHighPower) {
        // If checkbox is checked but field has a value (should be empty)
        if (
          hasSwitching === true &&
          value !== undefined &&
          value !== null &&
          (!(typeof value === "string") || value !== "")
        ) {
          return translations.globals.validationMessages.hasNoSwitchingOperation;
        }

        // If checkbox is unchecked but field is empty (should have a value)
        if (
          hasSwitching === false &&
          (value === undefined || value === null || (typeof value === "string" && value === ""))
        ) {
          return translations.globals.validationMessages.required;
        }
      }

      // For low power generators, validation is relaxed (fields are optional)
      // but we still check if the checkbox is checked and field has a value
      else if (
        isGenWithLowPower &&
        hasSwitching === true &&
        value !== undefined &&
        value !== null &&
        (!(typeof value === "string") || value !== "")
      ) {
        return translations.globals.validationMessages.hasNoSwitchingOperation;
      }
      return undefined;
    },
    triggerFields: [
      switchKey,
      "__category",
      "__normList",
      "maxActivePower",
      "hasActivePowerRange",
      "minActivePowerRange",
      "maxActivePowerRange",
    ],
    liveValidation: true,
  };

  // Rule for integer validation - always applies when value exists
  const integerValidationRule: FullRule<T_FIELD> = {
    rule: ({ value }) => {
      // Only validate if field has a value
      if (value === undefined || value === null || (typeof value === "string" && value === "")) {
        return undefined;
      } // Check if fieldName is one of the fields requiring integer validation
      if (fieldName.endsWith("MaxSwitchN10") || fieldName.endsWith("MaxNumberN120")) {
        return /^[0-9]+$/.test(value?.toString() ?? "0") ? undefined : translations.globals.validationMessages.integer;
      }

      return undefined;
    },
    triggerFields: [],
  };

  // Generic field validation rule - applies when isGen is false
  const genericFieldRule: FullRule<T_FIELD> = {
    rule: ({ value, data }) => {
      const category = data.__category;
      const isGeneratorWithHighNorm =
        (category === "ASYNCHRONOUS_GENERATOR" || category === "SYNCHRONOUS_GENERATOR") &&
        normListIncludes10OrHigher(data.__normList);
      const effectiveMaxActivePower = getEffectiveMaxActivePower(data);

      // Check if it's a generator with max power >= 30 (required fields)
      const isGenWithHighPower =
        isGeneratorWithHighNorm && typeof effectiveMaxActivePower === "number" && effectiveMaxActivePower >= 30;

      // Skip generic validation if we're in switching operation context with high power
      if (isGenWithHighPower) {
        return undefined;
      }

      // Apply isFieldRequired logic from makeGenericUnitFieldRule
      const fieldRequiredResult = isFieldRequied(fieldName as keyof UploadCertificateUnit)({ value, data });
      if (fieldRequiredResult) {
        return fieldRequiredResult;
      }

      // Apply checkFractionalDigits if needed
      const fractionalDigitsResult = checkFractionalDigits(fieldName as keyof UploadCertificateUnit)({ value, data });
      if (fractionalDigitsResult) {
        return fractionalDigitsResult;
      }

      // Apply additional generic rules if provided
      if (genericRule) {
        const rules = Array.isArray(genericRule) ? genericRule : [genericRule];
        for (const rule of rules) {
          const ruleFn = typeof rule === "function" ? rule : rule.rule;
          const result = ruleFn({ value, data });
          if (result) return result;
        }
      }

      return undefined;
    },
    triggerFields: [
      "__category",
      "__normList",
      "maxActivePower",
      "hasActivePowerRange",
      "minActivePowerRange",
      "maxActivePowerRange",
      ...(Array.isArray(genericRule)
        ? genericRule.flatMap((r) =>
            typeof r === "object" && r && "triggerFields" in r && Array.isArray(r.triggerFields)
              ? (r.triggerFields as TriggerField[])
              : []
          )
        : typeof genericRule === "object" && Array.isArray(genericRule.triggerFields)
        ? (genericRule.triggerFields as TriggerField[])
        : []),
    ],
  };

  return [switchingOperationRule, integerValidationRule, genericFieldRule];
}
